import { TimesheetEntry } from '../types';
import { parseLocalDate } from './dateUtils';

// ── Shared helpers ──

/**
 * Get week number for a date within its month.
 * Week 01 = day 1 of the month through the first Sunday (even if day 1 is mid-week).
 * Week 02+ = each subsequent Monday–Sunday block.
 * No orphan/week-0 days — every day in the month belongs to a week.
 *
 * Example — April 2026 (starts Wednesday):
 *   Week 01 → Wed 1 – Sun 5
 *   Week 02 → Mon 6 – Sun 12
 *   Week 03 → Mon 13 – Sun 19
 *   Week 04 → Mon 20 – Sun 26
 *   Week 05 → Mon 27 – Wed 30
 */
export function getWeekNumber(dateStr: string): number {
  const date = parseLocalDate(dateStr);
  const dayOfMonth = date.getDate();
  const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstDayOfWeek = firstOfMonth.getDay(); // 0=Sun, 1=Mon … 6=Sat

  // Days from day 1 to the first Sunday (end of Week 01)
  // If the month starts on Sunday, the first Sunday IS day 1 (0 days away)
  const daysToFirstSunday = firstDayOfWeek === 0 ? 0 : 7 - firstDayOfWeek;
  const firstSundayDate = 1 + daysToFirstSunday;

  // Week 01: day 1 through first Sunday
  if (dayOfMonth <= firstSundayDate) return 1;

  // Week 02+: Monday blocks after the first Sunday
  const firstMondayOfWeek2 = firstSundayDate + 1;
  return Math.floor((dayOfMonth - firstMondayOfWeek2) / 7) + 2;
}

/** Check if a date is in the future (compared to today, date-only).
 *  Uses manual date parsing to avoid UTC vs local timezone mismatch. */
export function isFutureDate(dateStr: string): boolean {
  const entryDate = parseLocalDate(dateStr);
  const today = new Date();
  entryDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return entryDate > today;
}

/** Parse "HH:mm" or "HH:mm:ss" to decimal hours. */
function timeToDecimalHours(time: string | null): number | null {
  if (!time) return null;
  const match = String(time).match(/(\d{2}):(\d{2})/);
  if (!match) return null;
  return parseInt(match[1]) + parseInt(match[2]) / 60;
}

/** Calculate total hours from start/finish time strings. Supports overnight shifts (finish < start wraps around midnight).
 *  Subtracts break time (in minutes, default 0) from the result. */
export function calculateTotalHours(startTime: string | null, finishTime: string | null, breakMinutes: number = 0): number {
  const start = timeToDecimalHours(startTime);
  const finish = timeToDecimalHours(finishTime);
  if (start === null || finish === null) return 0;
  let diff = finish - start;
  if (diff < 0) diff += 24; // overnight shift wraps around midnight
  diff = Math.max(diff - (breakMinutes / 60), 0); // subtract break, never go negative
  return Math.round(diff * 100) / 100;
}

/** Format decimal hours as H:MM. */
export function formatHoursMinutes(decimal: number): string {
  const abs = Math.abs(decimal);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  const sign = decimal < 0 ? '-' : '';
  return `${sign}${h}:${String(m).padStart(2, '0')}`;
}

// ── Types ──

export interface HardValidationError {
  type: 'hard';
  message: string;
}

export interface SoftWarning {
  type: 'soft';
  message: string;
}

export interface ValidationResult {
  errors: HardValidationError[];
  warnings: SoftWarning[];
}

export interface PendingChange {
  entryId: string;
  field: string;
  value: any;
  oldValue: any;
  rowNode: any;
  colField: string;
}

export interface SubmissionWarning {
  message: string;
  details: string[];
}

// ── Cell-change validation ──

/**
 * Validate a cell change before saving.
 * Returns hard errors (block save) and soft warnings (confirm to continue).
 */
export function validateEntryChange(
  rowData: any,
  field: string,
  value: any,
  allEntries: TimesheetEntry[],
): ValidationResult {
  const errors: HardValidationError[] = [];
  const warnings: SoftWarning[] = [];

  // Only run time-based validations when time fields change
  if (field === 'start_time' || field === 'finish_time' || field === '_time_update') {
    let startTime: string | null;
    let finishTime: string | null;

    if (field === '_time_update') {
      startTime = value.start_time ?? rowData.start_time ?? null;
      finishTime = value.finish_time ?? rowData.finish_time ?? null;
    } else if (field === 'start_time') {
      startTime = value || null;
      finishTime = rowData.finish_time || null;
    } else {
      startTime = rowData.start_time || null;
      finishTime = value || null;
    }

    // SOFT: Weekend entry — warn when adding time to a weekend day
    // Check the original entry from allEntries (rowData already has the new value)
    const entryDay = parseLocalDate(rowData.entry_date).getDay();
    const isWeekendDay = entryDay === 0 || entryDay === 6;
    if (isWeekendDay) {
      const originalEntry = allEntries.find(e => e.id === rowData.id);
      const hadNoTime = originalEntry && !originalEntry.start_time && !originalEntry.finish_time;
      if (hadNoTime) {
        const dayName = entryDay === 0 ? 'Sunday' : 'Saturday';
        warnings.push({ type: 'soft', message: `Did you work on ${dayName}?` });
      }
    }

    // Skip remaining time validations if either time is not set
    if (!startTime || !finishTime) return { errors, warnings };

    const breakMins = Number(rowData.break_minutes ?? 0);
    const newTotalHours = calculateTotalHours(startTime, finishTime, breakMins);

    // HARD: Total hours > 24
    if (newTotalHours > 24) {
      errors.push({ type: 'hard', message: 'Total hours for a day cannot exceed 24 hours.' });
      return { errors, warnings };
    }

    // SOFT: Entry < 15 minutes (0.25 hours)
    if (newTotalHours > 0 && newTotalHours < 0.25) {
      warnings.push({ type: 'soft', message: `This entry is less than 15 minutes (${formatHoursMinutes(newTotalHours)}).` });
    }

    // SOFT: Daily hours > 12
    if (newTotalHours > 12) {
      warnings.push({ type: 'soft', message: `Daily hours exceed 12 hours (${formatHoursMinutes(newTotalHours)}).` });
    }

    // SOFT: Daily hours < 8 (only when both times are set)
    if (newTotalHours > 0 && newTotalHours < 8) {
      warnings.push({ type: 'soft', message: `Daily hours are less than 8 hours (${formatHoursMinutes(newTotalHours)}).` });
    }

    // SOFT: Weekly total > 40
    const weekNum = getWeekNumber(rowData.entry_date);
    if (weekNum > 0) {
      const weeklyTotal = calculateWeeklyTotal(rowData.entry_date, rowData.id, newTotalHours, allEntries);
      if (weeklyTotal > 40) {
        warnings.push({ type: 'soft', message: `Weekly total exceeds 40 hours (${formatHoursMinutes(weeklyTotal)}).` });
      }
    }
  }

  return { errors, warnings };
}

/** Calculate weekly total with a pending change applied. */
function calculateWeeklyTotal(
  entryDate: string,
  entryId: string,
  newHours: number,
  allEntries: TimesheetEntry[],
): number {
  const targetWeek = getWeekNumber(entryDate);
  let total = 0;
  for (const e of allEntries) {
    if (getWeekNumber(e.entry_date) === targetWeek) {
      if (e.id === entryId) {
        total += newHours;
      } else {
        total += Number(e.total_hours || 0);
      }
    }
  }
  return total;
}

// ── Submission validation (download/email) ──

function formatDateForWarning(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

/**
 * Validate entries before downloading or emailing a timesheet.
 * Checks for missing task details and under-40 weekly totals.
 */
export function validateForSubmission(
  entries: TimesheetEntry[],
  weekFilter?: [number, number],
): SubmissionWarning[] {
  const warnings: SubmissionWarning[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = weekFilter
    ? entries.filter(e => {
        const wn = getWeekNumber(e.entry_date);
        return wn >= weekFilter[0] && wn <= weekFilter[1];
      })
    : entries;

  // Check: task_details empty for past/current dates that have time entries
  const missingTaskDetails: string[] = [];
  for (const entry of filtered) {
    const entryDate = parseLocalDate(entry.entry_date);
    entryDate.setHours(0, 0, 0, 0);
    if (entryDate <= today) {
      const hasTime = Number(entry.total_hours || 0) > 0;
      const noTask = !entry.task_details || entry.task_details.trim() === '';
      if (hasTime && noTask) {
        missingTaskDetails.push(formatDateForWarning(entry.entry_date));
      }
    }
  }
  if (missingTaskDetails.length > 0) {
    warnings.push({
      message: 'Task details are missing for dates that have time entries:',
      details: missingTaskDetails,
    });
  }

  // Check: weekly total < 40 hours (for weeks that have any entries)
  const weekTotals = new Map<number, number>();
  for (const entry of filtered) {
    const wn = getWeekNumber(entry.entry_date);
    if (wn > 0) {
      weekTotals.set(wn, (weekTotals.get(wn) || 0) + Number(entry.total_hours || 0));
    }
  }
  const underWeeks: string[] = [];
  weekTotals.forEach((total, week) => {
    if (total > 0 && total < 40) {
      underWeeks.push(`Week ${String(week).padStart(2, '0')} — ${formatHoursMinutes(total)}`);
    }
  });
  if (underWeeks.length > 0) {
    warnings.push({
      message: 'Some weeks have less than 40 total hours:',
      details: underWeeks,
    });
  }

  return warnings;
}
